import React from 'react';
import { Checkbox, Modal, Button, Select } from 'antd';
import { FormProvider, Controller } from 'react-hook-form';
import useAuthorizedDomainForm from './useAuthorizedDomainForm';
import { commonProps } from './useAuthorizedDomainForm';
import { Row, Input } from 'components/ui';
import InputLabel from 'components/blocks/BugFeatureReportForm/InputLabel';
import UploadCredentials from './UploadCredentials';
import { useAddGSuiteDomain, useUpdateGSuiteCredentials } from 'mutation/admin';
import { EMAIL_SERVICES } from '../../../../constants/common';

export default function AuthorizedDomainForm({ onHideForm, domainInfo }) {
  const isUpdate = !!domainInfo;
  const form = useAuthorizedDomainForm({ domainInfo });

  const onSubmitComplete = () => {
    form.reset();
    onHideForm();
  };

  const {
    isLoading: isAddingGSuiteDomain,
    mutate: addGSuiteDomain,
  } = useAddGSuiteDomain({
    onSuccess: onSubmitComplete,
  });

  const {
    isLoading: isUpdatingGSuiteCredentials,
    mutate: updateGSuiteCredentials,
  } = useUpdateGSuiteCredentials({
    onSuccess: onSubmitComplete,
  });

  const isLoading = isAddingGSuiteDomain || isUpdatingGSuiteCredentials;

  const onConfirmSubmit = values => {
    Modal.confirm({
      title: 'Confirmation',
      content: `Are you sure you want to continue?`,
      okText: 'Yes',
      okType: 'primary',
      onOk: () => submitValues(values),
      cancelText: 'No',
    });
  };

  const onAddGSuiteDomain = values => {
    // eslint-disable-next-line no-unused-vars
    const { credentials, isActive, credentialsUpdated, ...payload } = values;
    const formData = new FormData();
    formData.append('credentials', credentials);
    formData.append(
      'payload',
      JSON.stringify({
        ...payload,
        is_active: isActive,
      }),
    );

    addGSuiteDomain(formData);
  };

  const onUpdateGSuiteCredentials = values => {
    const { credentials, id, isActive, credentialsUpdated } = values;
    const formData = new FormData();
    formData.append('credentials', credentials);
    formData.append(
      'payload',
      JSON.stringify({
        is_active: isActive,
        credentials_updated: credentialsUpdated,
      }),
    );
    updateGSuiteCredentials({ id, formData });
  };

  const submitValues = values => {
    if (isUpdate) onUpdateGSuiteCredentials(values);
    else onAddGSuiteDomain(values);
  };

  return (
    <Modal
      footer={[
        <Button
          disabled={isLoading}
          key="submit"
          loading={isLoading}
          type="primary"
          onClick={form.handleSubmit(onConfirmSubmit)}
        >
          {isUpdate ? 'Update' : 'Add'}
        </Button>,
      ]}
      title={isUpdate ? 'Update Credentials' : 'Add Authorized Domain'}
      visible
      onCancel={onHideForm}
      onOk={form.handleSubmit(onConfirmSubmit)}
    >
      <FormProvider {...form}>
        <Row>
          <Controller
            control={form.control}
            name="name"
            render={({ field, fieldState }) => {
              return (
                <InputLabel label="Domain Name">
                  <Input
                    {...commonProps({ field, fieldState })}
                    disabled={isLoading || isUpdate}
                    type="text"
                  />
                </InputLabel>
              );
            }}
          />
        </Row>

        <Row>
          <Controller
            control={form.control}
            name="esp"
            render={({ field, fieldState }) => {
              return (
                <InputLabel label="ESP">
                  <Select
                    {...commonProps({ field, fieldState })}
                    disabled={isLoading || isUpdate}
                    style={{ width: '100%' }}
                  >
                    <Select.Option value={EMAIL_SERVICES.GMAIL}>
                      GSuite
                    </Select.Option>
                    <Select.Option value={EMAIL_SERVICES.MICROSOFT}>
                      Microsoft
                    </Select.Option>
                  </Select>
                </InputLabel>
              );
            }}
          />
        </Row>

        <Row>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Controller
              control={form.control}
              name="isActive"
              render={({ field, fieldState }) => {
                return (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginTop: 20,
                    }}
                  >
                    <InputLabel label="Is Active" />
                    <Checkbox
                      {...commonProps({ field, fieldState })}
                      checked={field.value}
                      disabled={isLoading}
                      style={{ marginTop: -25, paddingLeft: 5 }}
                    />
                  </div>
                );
              }}
            />

            <div style={{ marginTop: -5 }}>
              <UploadCredentials disabled={isLoading} isUpdate={isUpdate} />
            </div>
          </div>
        </Row>
      </FormProvider>
    </Modal>
  );
}
